import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Agent Interfaces ────────────────────────────────────────────────────────

export interface AgentResponse {
  id: string;
  agent_id: string;
  app_id: string;
  concept_ids: string[];
  content_runs_per_concept: number;
  trigger_interval: string;
  is_active: boolean;
  last_triggered_at: string | null;
  next_trigger_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentStatusResponse {
  agent_id: string;
  app_id: string;
  is_active: boolean;
  trigger_interval: string;
  last_triggered_at: string | null;
  next_trigger_at: string | null;
  next_trigger_in_seconds: number | null;
  current_run?: Record<string, unknown> | null;
}

export interface CreateAgentRequest {
  app_id: string;
  concept_ids?: string[];
  content_runs_per_concept: number;
  trigger_interval: string;
  is_active?: boolean;
}

export interface UpdateAgentRequest {
  app_id?: string | null;
  concept_ids?: string[] | null;
  content_runs_per_concept?: number | null;
  trigger_interval?: string | null;
  is_active?: boolean | null;
}

export interface ListAgentsParams {
  app_id?: string | null;
  is_active?: boolean | null;
  skip?: number;
  limit?: number;
}

// ─── Run Interfaces ──────────────────────────────────────────────────────────

export type RunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface TaskResultResponse {
  concept_id: string;
  concept_title: string;
  run_number: number;
  language: string | null;
  task_type: 'text' | 'image';
  status: RunStatus;
  content_id: string | null;
  image_path: string | null;
  error: string | null;
  completed_at: string | null;
}

export interface AgentRunResponse {
  id: string;
  run_id: string;
  agent_id: string;
  app_id: string;
  status: RunStatus;
  total_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
  progress: number;
  task_results: TaskResultResponse[];
  triggered_by: string;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ListRunsParams {
  agent_id?: string | null;
  status?: string | null;
  skip?: number;
  limit?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AgentService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/agents/`;
  private runsUrl = `${environment.apiUrl}/runs/`;

  // ── Agents ──────────────────────────────────────────────────────────────────

  list(params?: ListAgentsParams): Observable<AgentResponse[]> {
    let p = new HttpParams();
    if (params?.app_id != null && params.app_id !== '') p = p.set('app_id', params.app_id);
    if (params?.is_active != null) p = p.set('is_active', String(params.is_active));
    if (params?.skip != null) p = p.set('skip', String(params.skip));
    if (params?.limit != null) p = p.set('limit', String(params.limit));
    return this.http.get<AgentResponse[]>(this.baseUrl, { params: p });
  }

  get(agent_id: string): Observable<AgentResponse> {
    return this.http.get<AgentResponse>(`${this.baseUrl}${agent_id}`);
  }

  getStatus(agent_id: string): Observable<AgentStatusResponse> {
    return this.http.get<AgentStatusResponse>(`${this.baseUrl}${agent_id}/status`);
  }

  create(payload: CreateAgentRequest): Observable<AgentResponse> {
    return this.http.post<AgentResponse>(this.baseUrl, payload);
  }

  update(agent_id: string, payload: UpdateAgentRequest): Observable<AgentResponse> {
    return this.http.put<AgentResponse>(`${this.baseUrl}${agent_id}`, payload);
  }

  delete(agent_id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${agent_id}`);
  }

  trigger(agent_id: string): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}${agent_id}/trigger`, {});
  }

  // ── Runs ────────────────────────────────────────────────────────────────────

  listRuns(params?: ListRunsParams): Observable<AgentRunResponse[]> {
    let p = new HttpParams();
    if (params?.agent_id != null && params.agent_id !== '') p = p.set('agent_id', params.agent_id);
    if (params?.status != null && params.status !== '') p = p.set('status', params.status);
    if (params?.skip != null) p = p.set('skip', String(params.skip));
    if (params?.limit != null) p = p.set('limit', String(params.limit));
    return this.http.get<AgentRunResponse[]>(this.runsUrl, { params: p });
  }

  getRun(run_id: string): Observable<AgentRunResponse> {
    return this.http.get<AgentRunResponse>(`${this.runsUrl}${run_id}`);
  }
}
